import { useState } from 'react';
import { AlphaPicker, BlockPicker, ColorResult, GithubPicker, SketchPicker } from 'react-color';
import styled from 'styled-components';

const MOCKED_COLORS = [
  '#FEFFFE',
  '#EBEBEB',
  '#D6D6D6',
  '#C2C2C2',
  '#ADADAD',
  '#999999',
  '#858585',
  '#707070',
  '#5C5C5C',
  '#474747',
  '#333333',
  '#000000',

  '#00374A',
  '#011D57',
  '#11053B',
  '#2E063D',
  '#3C071B',
  '#5C0701',
  '#5A1C00',
  '#583300',
  '#563D00',
  '#666100',
  '#4F5504',
  '#263E0F',

  '#004D65',
  '#012F7B',
  '#1A0A52',
  '#450D59',
  '#551029',
  '#831100',
  '#7B2900',
  '#7A4A00',
  '#785800',
  '#8D8602',
  '#6F760A',
  '#38571A',

  '#016E8F',
  '#0042A9',
  '#2C0977',
  '#61187C',
  '#791A3D',
  '#B51A00',
  '#AD3E00',
  '#A96800',
  '#A67B01',
  '#C4BC00',
  '#9BA50E',
  '#4E7A27',

  '#008CB4',
  '#0056D6',
  '#371A94',
  '#7A219E',
  '#99244F',
  '#E22400',
  '#DA5100',
  '#D38301',
  '#D19D01',
  '#F5EC00',
  '#C3D117',
  '#669D34',

  '#00A1D8',
  '#0061FD',
  '#4D22B2',
  '#982ABC',
  '#B92D5D',
  '#FF4015',
  '#FF6A00',
  '#FFAB01',
  '#FCC700',
  '#FEFB41',
  '#D9EC37',
  '#76BB40',

  '#01C7FC',
  '#3A87FD',
  '#5E30EB',
  '#BE38F3',
  '#E63B7A',
  '#FE6250',
  '#FE8648',
  '#FEB43F',
  '#FECB3E',
  '#FFF76B',
  '#E4EF65',
  '#96D35F',

  '#52D6FC',
  '#74A7FF',
  '#864FFD',
  '#D357FE',
  '#EE719E',
  '#FF8C82',
  '#FEA57D',
  '#FEC777',
  '#FED977',
  '#FFF994',
  '#EAF28F',
  '#B1DD8B',

  '#93E3FC',
  '#A7C6FF',
  '#B18CFE',
  '#E292FE',
  '#F4A4C0',
  '#FFB5AF',
  '#FFC5AB',
  '#FED9A8',
  '#FDE4A8',
  '#FFFBB9',
  '#F1F7B7',
  '#CDE8B5',

  '#CBF0FF',
  '#D2E2FE',
  '#D8C9FE',
  '#EFCAFE',
  '#F9D3E0',
  '#FFDAD8',
  '#FFE2D6',
  '#FEECD4',
  '#FEF1D5',
  '#FDFBDD',
  '#F6FADB',
  '#DEEED4'
];

const getColorWithAlpha = (hex: string, alpha: number) => {
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${alphaHex}`;
};

const hexToRgb = (hex: string, alpha: number) => {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b, a: alpha };
};

const githubPickerStyles = {
  default: {
    card: {
      display: 'grid',
      gridTemplateColumns: 'repeat(12, 1fr)',
      borderRadius: '8px',
      border: 'none',
      boxShadow: 'none'
    }
  }
};

const sketchPickerStyles = {
  default: {
    picker: {
      border: 'none',
      boxShadow: 'none'
    }
  }
};

type TabType = 'Grid' | 'Spectrum' | 'Slider';

const tabs: TabType[] = ['Grid', 'Spectrum', 'Slider'];

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
}

const ColorPicker = ({ color, onChange }: ColorPickerProps) => {
  const [hexColor, setHexColor] = useState<string>(() => `#${color}`);
  const [alpha, setAlpha] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<TabType>('Grid');

  const handleColorChange = (newColor: ColorResult) => {
    setHexColor(newColor.hex);
    onChange(newColor.hex?.replace('#', '') ?? '');
    if (newColor.rgb && newColor.rgb.a !== undefined) {
      setAlpha(newColor.rgb.a);
    }
  };

  return (
    <PickerContainer>
      <Title>Colors</Title>
      <Tabs>
        {tabs.map((tab) => (
          <TabButton key={tab} onClick={() => setActiveTab(tab)} active={activeTab === tab}>
            {tab}
          </TabButton>
        ))}
      </Tabs>
      <div>
        {activeTab === 'Grid' && (
          <>
            <GithubPicker colors={MOCKED_COLORS} onChangeComplete={handleColorChange} triangle="hide" width="100%" styles={githubPickerStyles} />
            <AlphaPicker color={hexToRgb(hexColor, alpha)} onChange={handleColorChange} width="100%" />
          </>
        )}
        {activeTab === 'Spectrum' && <SketchPicker color={hexColor} onChange={handleColorChange} width="93%" styles={sketchPickerStyles} />}
        {activeTab === 'Slider' && <BlockPicker color={hexColor} onChangeComplete={handleColorChange} width="100%" triangle="hide" />}
      </div>
    </PickerContainer>
  );
};

export default ColorPicker;

const PickerContainer = styled.div`
  padding: 20px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  width: 350px;
`;

const Title = styled.h3`
  text-align: center;
  font-weight: 600;
  margin-bottom: 20px;
`;

const Tabs = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  border-radius: 8px;
  padding: 2px;
  background-color: rgba(118, 118, 128, 0.12);
`;

const TabButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 8px 0;
  background: none;
  cursor: pointer;
  font-weight: 600;
  border-radius: 7px;
  border: ${({ active }) => (active ? '0.5px solid rgba(0, 0, 0, 0.04)' : 'none')};
  box-shadow: ${({ active }) => (active ? ' 0px 3px 8px 0px rgba(0, 0, 0, 0.12), 0px 3px 1px 0px rgba(0, 0, 0, 0.04)' : 'none')};
  background-color: ${({ active }) => (active ? '#fff' : 'transparent')};

  &:hover {
    opacity: 0.8;
  }
`;
